import { ActivePromises } from '../../Utility/ActivePromises';
import { Log } from '../../AuxillaryService/Log';
import { glom } from '../../Signage/glom';
import { Signage } from '../../Signage';
import { LocalCache } from '../LocalCache';
import { Asset } from '../Asset';
import { $ } from '../../../vendor/jquery';
import * as shortCircuit from './ShortCircuit';

/**
 * Failure Response
 *
 * Returns 4 values:
 *
 * 1. {XMLHttpRequest} xhr
 * 2. {string} textStatus
 * 3. {string} msg
 * 4. {Event} event
 * @typedef {jQuery.Promise} jqpFailureImageCache
 */

/**
 * @class
 * @hideconstructor
 */
const ImageCache = {};

/**
 * Track active image retrievals (to prevent duplicate downloads of the same
 * image before it's cached)
 * @type {ActivePromises}
 * @private
 */
var _activePromises = new ActivePromises('url');

/**
 * Cached Image Call with Fallbacks
 *
 * Attempt to get image from the REACH Signage Software.  If the REACH Signage
 * Software is not available, try to pull the image from `LocalCache`.
 * @param {string} url
 * @throws {jqpFailureImageCache}
 * @returns {jQuery.Promise<string, string>}
 *      Success `jQuery.Promise[{string} url, {string} textStatus]`
 */
ImageCache.getCached = function (url) {
    var activePromiseItem = { url: url };

    var currentlyActiveItem = _activePromises.get(activePromiseItem);
    if (currentlyActiveItem) {
        return currentlyActiveItem.promise;
    }

    var jqDeferred = $.Deferred();
    var jqPromise = jqDeferred.promise();

    activePromiseItem.promise = jqPromise;
    _activePromises.add(activePromiseItem);

    if (glom.caching === false) {
        jqDeferred.resolve(url, 'noCache');
    } else if (Signage.platform.getReachImageAssetUrl) {
        Signage.platform
            .getReachImageAssetUrl(url)
            .then((signageAssetUrl) => {
                // image is loaded
                jqDeferred.resolve(signageAssetUrl, 'reachasset');
            })
            .catch((error) => {
                // image is not loaded);
                jqDeferred.reject(null, 'reachassetError', error.message, error.detail.event);
            });
    } else {
        // Get cached asset via `LocalCache`

        LocalCache.get(url)
            .done(function (blob) {
                // got data from cache
                var dataURL = URL.createObjectURL(blob);
                jqDeferred.resolve(dataURL, 'cache');
            })
            .fail(function (msg, event) {
                // no data in cache
                var retryAttemptsExceeded = false;
                if (shortCircuit.inLedger(url)) {
                    retryAttemptsExceeded = shortCircuit.hasHitMaxAttempts(url);
                    shortCircuit.trackAttempt(url);
                }

                if (retryAttemptsExceeded) {
                    jqDeferred.reject(
                        null,
                        '',
                        'The image has reached its max retry count and no further download attempts will be made.',
                        null
                    );
                } else {
                    Log.Info(
                        'Image not found in cache, will attempt to download.',
                        'SIGNAGE_ASSET_CACHE_MISS',
                        __fileline,
                        { url: url }
                    );

                    _smartGetBlob(url)
                        .done(function (blob, statusText, xhr) {
                            // Put the received blob into IndexedDB
                            LocalCache.save(url, blob)
                                .done(function () {
                                    var dataURL = URL.createObjectURL(blob);
                                    jqDeferred.resolve(dataURL, 'cache');
                                })
                                .fail(function (msg, event) {
                                    // fail
                                    jqDeferred.reject(xhr, xhr.statusText, msg, event);
                                });
                        })
                        .fail(function (xhr, statusText) {
                            jqDeferred.reject(xhr, statusText, msg, event);
                        });
                }
            });
    }

    return jqPromise;
};

/**
 * @see Asset.blobToDataURL
 * @deprecated since version 0.0.0.11
 */
ImageCache.blobToDataURL = function () {
    return Asset.blobToDataURL.apply(this, arguments);
};

/**
 * Create Promise Fail Object for Logging Purposes
 * @param {XMLHttpRequest} xhr
 * @param {string} textStatus
 * @param {string} msg
 * @param {Event} event
 * @returns {{API: {xhr: *, textStatus: *}, CACHE: {message: *, event: *}}}
 */
ImageCache.createPromiseFailObject = function (xhr, textStatus, msg, event) {
    return {
        API: {
            xhr: xhr,
            textStatus: textStatus,
        },
        CACHE: {
            message: msg,
            event: event,
        },
    };
};

/**
 * @see Asset.getBlob
 * @deprecated since version 0.0.0.11
 */
ImageCache.getBlob = function () {
    return Asset.getBlob.apply(this, arguments);
};

/**
 * @see Asset.retrieve
 * @private
 * @deprecated since version 0.0.0.11
 */
var _smartGetBlob = function () {
    return Asset.retrieve.apply(this, arguments);
};

export { ImageCache };
